import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, type ApiError, type ChatTurn, type ListFlowsResult } from '@whinerp/client';
import { Button, SectionLabel } from '@whinerp/ui';
import { useErrorMessage, useT } from '../../app/i18n.js';

/**
 * A chat embed's script, and a place to try it (G-185).
 *
 * The try-out talks to the same public endpoints a host page does, with a
 * preview token - so what it answers here is what a visitor gets.
 */

const INPUT = 'h-input rounded-control border border-control bg-surface px-2 text-body text-ink';

export function ChatScriptPanel({ embedId, chatFlowId }: { embedId: string; chatFlowId: string | null }): JSX.Element {
  const t = useT();
  const errorText = useErrorMessage();
  const queryClient = useQueryClient();
  const scripts = useQuery<ListFlowsResult, ApiError>({
    queryKey: ['flows', 'chatbot'],
    queryFn: () => api.listFlows({ query: { palette: 'chatbot' } }),
  });
  const [chosen, setChosen] = useState<string>(chatFlowId ?? '');
  const [turn, setTurn] = useState<ChatTurn | null>(null);
  const [session, setSession] = useState<{ token: string; visitor: string } | null>(null);
  const [text, setText] = useState('');

  const save = useMutation({
    mutationFn: () => api.setChatScript({ path: { embedId }, body: { flowId: chosen || null } }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['embeds'] }),
  });
  const start = useMutation({
    mutationFn: async () => {
      const minted = await api.previewEmbed({ path: { embedId }, body: {} });
      const visitor = `preview-${Math.random().toString(36).slice(2, 12)}`;
      setSession({ token: minted.token, visitor });
      return api.startChat({ body: { token: minted.token, visitor } });
    },
    onSuccess: setTurn,
  });
  const reply = useMutation({
    mutationFn: (message: string) =>
      api.visitorSays({ body: { token: session!.token, visitor: session!.visitor, conversationId: turn!.conversationId, text: message } }),
    onSuccess: (next) => {
      setTurn(next);
      setText('');
    },
  });

  return (
    <div className="flex flex-col gap-2" data-chat-script={embedId}>
      <SectionLabel>{t('chatbot.script')}</SectionLabel>
      <div className="flex flex-wrap items-center gap-2">
        <select className={INPUT} data-chat-script-select aria-label={t('chatbot.script')} value={chosen} onChange={(event) => setChosen(event.target.value)}>
          <option value="">{t('chatbot.none')}</option>
          {(scripts.data?.flows ?? []).map((flow) => <option key={flow.flowId} value={flow.flowId}>{flow.name}</option>)}
        </select>
        <Button size="small" data-chat-script-save disabled={save.isPending} onClick={() => save.mutate()}>{t('chatbot.save')}</Button>
        <Button size="small" data-chat-try disabled={start.isPending} onClick={() => start.mutate()}>{t('chatbot.try')}</Button>
      </div>
      {turn ? (
        <div className="flex flex-col gap-1 rounded-chip bg-surface p-2" data-chat-try-window data-chat-status={turn.status}>
          <ol className="flex flex-col gap-1 text-dense">
            {turn.messages.map((message) => (
              <li key={message.messageId} data-chat-line={message.author}
                className={message.author === 'visitor' ? 'self-end text-ink' : 'text-ink-600'}>
                {message.body}
              </li>
            ))}
          </ol>
          {turn.awaiting?.kind === 'choice' ? (
            <div className="flex flex-wrap gap-1">
              {turn.awaiting.options.map((option) => (
                <Button key={option} size="small" data-chat-choice={option} onClick={() => reply.mutate(option)}>{option}</Button>
              ))}
            </div>
          ) : turn.status === 'bot' || turn.status === 'waiting' || turn.status === 'active' ? (
            <form className="flex gap-2" data-chat-try-form
              onSubmit={(event) => {
                event.preventDefault();
                if (text.trim()) reply.mutate(text);
              }}>
              <input className={`${INPUT} flex-1`} data-chat-try-input aria-label={t('chatbot.message')} value={text} onChange={(event) => setText(event.target.value)} />
              <Button size="small" type="submit" data-chat-try-send>{t('chatbot.send')}</Button>
            </form>
          ) : null}
        </div>
      ) : null}
      {[save.error, start.error, reply.error].map((error, index) => (error ? <p key={index} className="text-meta text-danger">{errorText(error as ApiError)}</p> : null))}
    </div>
  );
}
